import { EntityType } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { ReviewExtendedClient } from '@frontend/review/api';
import { IExtendedReview } from '@frontend/review/types';

import { MultiEntityButton } from '../../../components/multi-entity-button/multi-entity-button.component';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import OverviewHeader from '../../header/overview-header';
import { useReviews } from './reviews.controller';

const ID = 'vending-reviews';

const Reviews = () => {
    const viewProps = useReviews();
    const { openPlatformDetail } = usePlatformModalHost();

    return (
        <div
            id={ID}
            key={ID}
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader title='Reviews' />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <ReactiveObjectList<IExtendedReview>
                    id={ID}
                    dispatch={viewProps.dispatch}
                    fetch={(arg) => ReviewExtendedClient.fetchExtendedReviews(arg)}
                    type={EntityType.REVIEW}
                    eventListener={EntityType.REVIEW}
                    sortingColumns={['outcome', 'creation_timestamp']}
                    onSelectElement={(obj) =>
                        openPlatformDetail({
                            entityType: EntityType.REVIEW,
                            id: obj.id
                        })
                    }
                    columns={{
                        columns: ['user', 'product', 'categories', 'outcome', 'expected', 'actual', 'creation_timestamp', 'feature'],
                        customCells: new Map([
                            [
                                'user',
                                (obj) =>
                                    obj.row.original.user ? (
                                        <td
                                            className={defaultCellClassName}
                                            key={obj.row.original.user.entity_id}>
                                            <PlatformObjectBadge
                                                display={obj.row.original.user.name}
                                                entityType={obj.row.original.user.entity_type}
                                                id={obj.row.original.user.entity_id}
                                                key={obj.row.original.user.entity_id}
                                                foreign={obj.row.original.user.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.user)}
                                            />
                                        </td>
                                    ) : (
                                        <td></td>
                                    )
                            ],
                            [
                                'product',
                                (obj) =>
                                    obj.row.original.product ? (
                                        <td
                                            className={defaultCellClassName}
                                            key={obj.row.original.product.entity_id}>
                                            <PlatformObjectBadge
                                                display={obj.row.original.product.name}
                                                entityType={obj.row.original.product.entity_type}
                                                id={obj.row.original.product.entity_id}
                                                key={obj.row.original.product.entity_id}
                                                foreign={obj.row.original.product.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.product)}
                                            />
                                        </td>
                                    ) : (
                                        <td></td>
                                    )
                            ],
                            [
                                'categories',
                                (obj) => {
                                    if (obj.row.original.categories === null || obj.row.original.categories.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.categories[0].entity_id}
                                            className={defaultCellClassName}>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.categories[0].entity_id}
                                                    entityType={EntityType.PRODUCT_CATEGORY}
                                                    display={obj.row.original.categories[0].name}
                                                    key={obj.row.original.categories[0].entity_id}
                                                    foreign={obj.row.original.categories[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.categories[0])}
                                                />
                                                {obj.row.original.categories.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.categories.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.PRODUCT_CATEGORY}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'feature',
                                (obj) => {
                                    if (obj.row.original.feature === Feature.RECYCLE_TRANSACTION) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <span>{CommonMessage.OBJECTS.RECYCLE.DEFAULT}</span>
                                            </td>
                                        );
                                    } else if (obj.row.original.feature === Feature.RETURN_TRANSACTION || obj.row.original.feature === Feature.REVIEW) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <span>{CommonMessage.OBJECTS.RETURN.DEFAULT}</span>
                                            </td>
                                        );
                                    } else {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <span>{obj.row.original.feature}</span>
                                            </td>
                                        );
                                    }
                                }
                            ]
                        ])
                    }}
                    activeFilter={{
                        searchKeys: ['user'],
                        filterKeys: ['review_outcome'],
                        queryKeyMap: new Map([['review_outcome', 'outcome']])
                    }}
                    showCount
                />
            </div>
        </div>
    );
};

export default Reviews;
